import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#sortGridBySku();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#isRenderingNextPage) return;

    this.#isRenderingNextPage = true;

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const uniqueNextPageItemElements = this.#filterDuplicateCards(nextPageItemElements);
      if (!uniqueNextPageItemElements.length) return;

      grid.append(...uniqueNextPageItemElements);

      this.#aspectRatioHelper.processNewElements();
      this.#sortGridBySku();

      history.pushState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#isRenderingNextPage = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#isRenderingPreviousPage) return;

    this.#isRenderingPreviousPage = true;

    try {
      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      const uniquePreviousPageItemElements = this.#filterDuplicateCards(previousPageItemElements);
      if (!uniquePreviousPageItemElements.length) return;

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...uniquePreviousPageItemElements);

      this.#aspectRatioHelper.processNewElements();
      this.#sortGridBySku();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    const cards = this.#getCurrentCards();

    if (!cards.length) return;

    const loadedPages = cards
      .map((card) => Number(card.dataset.page))
      .filter((page) => Number.isFinite(page));

    if (!loadedPages.length) return;

    const page = isPrevious ? Math.min(...loadedPages) - 1 : Math.max(...loadedPages) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Reads current cards from the live grid. This must not depend on cached refs
   * because collection cards can be appended, prepended, and re-sorted.
   *
   * @returns {Element[]}
   */
  #getCurrentCards() {
    const { grid } = this.refs;
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  /**
   * @param {Element} card
   * @returns {string}
   */
  #getProductCardKey(card) {
    const productId = card.getAttribute('data-product-id');
    if (productId) return `id:${productId}`;

    const productLink = card.querySelector('a[href*="/products/"]');
    if (!(productLink instanceof HTMLAnchorElement)) return '';

    try {
      const url = new URL(productLink.href);
      return `url:${url.pathname}`;
    } catch (_error) {
      return '';
    }
  }

  /**
   * Prevents the same product card from being inserted twice when scroll
   * observers fire around an already-loaded page.
   *
   * @param {NodeListOf<Element>} cards
   * @returns {Element[]}
   */
  #filterDuplicateCards(cards) {
    const existingKeys = new Set(this.#getCurrentCards().map((card) => this.#getProductCardKey(card)).filter(Boolean));
    const uniqueCards = [];

    for (const card of Array.from(cards)) {
      const key = this.#getProductCardKey(card);
      if (key && existingKeys.has(key)) continue;

      if (key) existingKeys.add(key);
      uniqueCards.push(card);
    }

    return uniqueCards;
  }

  /**
   * Date-sort pages must keep Shopify's native product-date order.
   * SKU numbers are inventory codes, not upload timestamps.
   *
   * @returns {undefined}
   */
  #getSkuSortDirection() {
    return undefined;
  }

  /**
   * Extracts the numeric portion we use for SKU sorting.
   * Larger numbers are treated as newer.
   *
   * @param {Element} card
   * @returns {number}
   */
  #getSkuSortValue(card) {
    const rawSku = card.getAttribute('data-sku-sort-value')?.trim() ?? '';
    if (!rawSku) return -1;

    const numericGroups = rawSku.match(/\d+/g);
    if (!numericGroups?.length) return -1;

    const value = Number(numericGroups[numericGroups.length - 1]);
    return Number.isNaN(value) ? -1 : value;
  }

  /**
   * @param {Element} card
   * @returns {boolean}
   */
  #isUnavailableGridCard(card) {
    const availableValue = card.getAttribute('data-product-available');
    return availableValue === 'false' || card.classList.contains('product-grid__item--sold-out');
  }

  /**
   * Keeps the user's viewport anchored when reordering cards.
   *
   * @param {Element[] | undefined} cards
   * @returns {{ element: Element, top: number } | undefined}
   */
  #getScrollAnchor(cards) {
    if (!cards?.length) return;

    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (rect.bottom > 0) {
        return { element: card, top: rect.top };
      }
    }
  }

  /**
   * @param {{ element: Element, top: number } | undefined} anchor
   */
  #restoreScrollAnchor(anchor) {
    if (!anchor?.element.isConnected) return;

    const newTop = anchor.element.getBoundingClientRect().top;
    const delta = newTop - anchor.top;
    if (!delta) return;

    window.scrollTo({
      top: window.scrollY + delta,
      behavior: 'instant',
    });
  }

  /**
   * Keeps buyable items ahead of sold-out products.
   * This runs after initial render, filter updates, and infinite-scroll page inserts.
   */
  #sortGridBySku() {
    const skuSortDirection = this.#getSkuSortDirection();
    const { grid } = this.refs;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    if (cards.length < 2) return;

    const hasUnavailableCards = cards.some((card) => this.#isUnavailableGridCard(card));
    if (!skuSortDirection && !hasUnavailableCards) return;

    const anchor = this.#getScrollAnchor(cards);
    const originalOrder = new Map(cards.map((card, index) => [card, index]));

    cards.sort((cardA, cardB) => {
      const unavailableA = this.#isUnavailableGridCard(cardA);
      const unavailableB = this.#isUnavailableGridCard(cardB);
      if (unavailableA !== unavailableB) return unavailableA ? 1 : -1;

      if (skuSortDirection) {
        const skuA = this.#getSkuSortValue(cardA);
        const skuB = this.#getSkuSortValue(cardB);

        if (skuA !== skuB) {
          return skuSortDirection === 'ascending' ? skuA - skuB : skuB - skuA;
        }
      }

      const pageA = Number(cardA.getAttribute('data-page') ?? 0);
      const pageB = Number(cardB.getAttribute('data-page') ?? 0);
      if (pageA !== pageB) return pageA - pageB;

      return (originalOrder.get(cardA) ?? 0) - (originalOrder.get(cardB) ?? 0);
    });

    const fragment = document.createDocumentFragment();
    for (const card of cards) {
      fragment.append(card);
    }

    grid.append(fragment);
    this.#restoreScrollAnchor(anchor);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#sortGridBySku();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
